/* =========================================================================
   ADORN: header search popdown fixes
   SEARCH-001  size the results panel to the visible viewport (keyboard-aware)
   SEARCH-003  the X clears the typed text instead of leaving it behind
   SEARCH-005  category links go to the category, not to a keyword search
   SEARCH-006  a visible empty state instead of a blank panel
   SEARCH-007  products get their own share of the dropdown, not 2 of 8 slots
   SEARCH-008  the top group is collections (categories), not query completions

   SEARCH-001
   ----------
   Sets --search-panel-max-height on each .search-popdown__results from
   window.visualViewport, which shrinks when the on-screen keyboard opens.
   assets/adorn-search-fix.css consumes it. See that file for the full why.

   SEARCH-003
   ----------
   Palo Alto's SearchPopdown.close() calls searchFormWrapper.close() with no
   argument. That lands in closeResults(false), and only closeResults(true)
   calls reset(). So the input keeps its value after the popdown is dismissed:
   reopen the search and yesterday's query is still sitting there, and because
   onFocus() re-runs currentSearchTerm it immediately refires that old search.

   New behaviour for the X, which is the standard mobile search pattern:
     field has text  -> clear the text, drop the results, keep the panel open,
                        put the caret back in the field
     field is empty  -> close the popdown, exactly as before
   Closing by any other route (outside tap, Escape, the search icon) now also
   resets the field, so the panel always reopens clean.

   Everything here hooks from the outside - capture-phase listeners on
   document, a MutationObserver, and one narrow fetch wrapper - so it runs
   before the theme's own handlers, covers all three search-popdown instances
   the header renders (desktop, compressed desktop, mobile menu), and never
   needs theme.js to be edited. See each section below for its own reasoning.

   Remove this file + adorn-search-fix.css + their two tags in
   layout/theme.liquid to roll back.
   ========================================================================= */
(function () {
  'use strict';

  var GUTTER = 12;
  var MIN_HEIGHT = 160;

  /* ----------------------------------------------------------------
     SEARCH-001 - keyboard-aware panel height
     ---------------------------------------------------------------- */
  function syncPanelHeight() {
    var vv = window.visualViewport;
    var viewportHeight = vv ? vv.height : window.innerHeight;
    var viewportTop = vv ? vv.offsetTop : 0;
    var panels = document.querySelectorAll('.search-popdown__results');

    for (var i = 0; i < panels.length; i++) {
      var panel = panels[i];
      var rect = panel.getBoundingClientRect();

      /* A closed <details> does not render its contents, so the panel reports
         an all-zero rect. Measuring then would store a full-viewport height
         and briefly show an oversized panel on the next open. Skip it: the
         toggle listener below re-measures once it is actually laid out. */
      if (rect.top === 0 && rect.height === 0) continue;

      /* getBoundingClientRect is relative to the layout viewport; subtract
         offsetTop to get the position within the visible one. */
      var top = rect.top - viewportTop;
      var available = Math.max(MIN_HEIGHT, Math.round(viewportHeight - top - GUTTER));
      panel.style.setProperty('--search-panel-max-height', available + 'px');
    }
  }

  var queued = false;
  function queueSync() {
    if (queued) return;
    queued = true;
    window.requestAnimationFrame(function () {
      queued = false;
      syncPanelHeight();
    });
  }

  if (window.visualViewport) {
    window.visualViewport.addEventListener('resize', queueSync);
    window.visualViewport.addEventListener('scroll', queueSync);
  }
  window.addEventListener('resize', queueSync);
  window.addEventListener('orientationchange', queueSync);
  document.addEventListener('theme:scroll', queueSync, { passive: true });

  /* The panel only has a measurable position once the popdown is open, and the
     popdown animates in, so measure again on the way through.

     'toggle' does not bubble, but a capture-phase listener on document still
     sees it: capture runs root -> target for every event. That covers all
     three header popdowns without binding to each one. */
  document.addEventListener(
    'toggle',
    function (event) {
      if (!event.target || !event.target.closest) return;
      if (!event.target.closest('search-popdown')) return;
      queueSync();
      setTimeout(syncPanelHeight, 400); /* after the open animation settles */
    },
    true
  );

  document.addEventListener('focusin', function (event) {
    if (event.target && event.target.matches && event.target.matches('input[type="search"]')) {
      queueSync();
      /* iOS animates the keyboard in over ~300ms and only then settles
         visualViewport.height. One trailing measurement catches the end state
         on browsers that do not fire a final resize. */
      setTimeout(syncPanelHeight, 350);
    }
  });

  /* The header compresses on scroll, which moves the panel's top edge. */
  document.addEventListener(
    'transitionend',
    function (event) {
      if (event.target && event.target.matches && event.target.matches('[data-search-form-inner]')) {
        queueSync();
      }
    },
    true
  );

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', syncPanelHeight);
  } else {
    syncPanelHeight();
  }

  /* ----------------------------------------------------------------
     SEARCH-003 - the X clears the field
     ---------------------------------------------------------------- */
  function fieldOf(popdown) {
    return popdown.querySelector('input[type="search"]');
  }

  function wrapperOf(popdown) {
    return popdown.querySelector('predictive-search') || popdown.querySelector('search-form');
  }

  function clearSearch(popdown) {
    var input = fieldOf(popdown);
    var wrapper = wrapperOf(popdown);
    if (!input) return;

    input.value = '';

    if (wrapper) {
      /* close(true) -> closeResults(true) -> reset() + drops [results]/[open],
         which is the path the theme never takes on its own. */
      if (typeof wrapper.close === 'function') {
        try { wrapper.close(true); } catch (e) { /* older bundle, ignore */ }
      }
      wrapper.classList.remove('is-searched');
      wrapper.classList.remove('reset');
      wrapper.searchTerm = '';
      wrapper.currentSearchTerm = '';
    }
  }

  document.addEventListener(
    'click',
    function (event) {
      var target = event.target;
      if (!target || !target.closest) return;

      var button = target.closest('[data-popdown-close]');
      if (!button) return;

      var popdown = button.closest('search-popdown');
      if (!popdown) return;

      var input = fieldOf(popdown);
      if (!input || !input.value.length) return; /* empty: let the theme close it */

      /* Stopping propagation in the capture phase keeps the event from ever
         reaching the theme's own listener on the button, so the popdown stays
         open and this tap only clears. */
      event.preventDefault();
      event.stopPropagation();

      clearSearch(popdown);
      input.focus();
      queueSync();
    },
    true
  );

  /* Any other dismissal route - outside tap, Escape, the search icon - ends in
     this event. Reset there too so the panel always reopens on an empty field.

     Scoped to [data-popdown-in-header] on purpose. The search RESULTS page
     renders a second, in-page popdown whose input is pre-filled with
     search.terms; wiping that would blank the query the customer is currently
     looking at. Only the header popdown is cleared. */
  document.addEventListener('theme:search:close', function () {
    var popdowns = document.querySelectorAll('search-popdown[data-popdown-in-header]');
    for (var i = 0; i < popdowns.length; i++) clearSearch(popdowns[i]);
  });

  /* ----------------------------------------------------------------
     SEARCH-005 - category links go to the category

     The panel's "Suggested search terms" and "Browse categories" links are
     rendered by snippets/popular-searches.liquid as real anchors pointing at
     real collections (/collections/gift-for-baby, /collections/baby-jewellery,
     /pages/corporate-gifting ...). theme.js then does this to every one:

         t.preventDefault();
         this.searchFormWrapper.input.value = t.target.textContent;
         this.searchFormWrapper.submit();

     ...so tapping "New Born Gifts" does NOT open the New Born collection. It
     runs a keyword search for the words "New Born Gifts" and drops you on a
     relevance-ranked results page instead of the merchandised collection.

     Clarity, 30 days to 2026-08-28: 57 searches for "new born gifts", 15 for
     "wedding and anniversary gifts", 12 "birthday gifts", 12 "baby jewellery",
     5 "festive collection", 2 "all gifting", 2 "housewarming gifts". Those are
     not customers typing - those are category taps being turned into keyword
     searches. Over 100 of ~779 monthly search sessions.

     Fix: stop the event in the capture phase so the theme's preventDefault
     never runs, and let the browser follow the href it was already given.
     ---------------------------------------------------------------- */
  document.addEventListener(
    'click',
    function (event) {
      if (!event.target || !event.target.closest) return;
      var link = event.target.closest('[data-popular-searches-link]');
      if (!link) return;
      if (!link.getAttribute('href')) return; /* nothing to navigate to */

      /* No preventDefault here - that is the whole point. Stopping propagation
         keeps the theme's handler from hijacking the click; the anchor then
         does what an anchor does. */
      event.stopPropagation();
    },
    true
  );

  /* ----------------------------------------------------------------
     SEARCH-007 / SEARCH-008 - what the dropdown asks Shopify for

     Shopify's predictive-search `resources[limit]` is a budget shared across
     ALL requested resource types, and query suggestions are served first.
     theme.js asks for query,article,collection,product,page with limit 8, so
     on a broad term the queries eat the panel:

       "rakhi" (the #1 on-site search, 160/month) -> 4 suggestions,
                                                     2 products, 2 blog posts
       "coin"                                     -> 5 suggestions,
                                                     2 products, 1 blog post

     Two products, on a term with 45 matching products. Raising the limit does
     not help - measured, the extra slots just go to more query suggestions.

     The API takes `resources[limit_scope]=each`, which applies the limit PER
     type instead. Same request, same render path, products get their own 8.
     Measured after: "rakhi" 6 products, "coin" 6, "baby" 6, "idols" 6.

     SEARCH-008 - drop the `query` resource type

     The section template merges two different things into one group headed
     "Suggestions": Shopify's `queries` (auto-generated search-term
     completions, mined largely from product titles) and `collections`. The
     queries are returned first, so in practice they were the whole group -
     and they read as product names sitting directly above the product list
     showing the same products:

       "silver bar" -> silver bar, sacred banyan tree silver bar, silver bars,
                       evil eye silver bar, forever thoughtful silver gift bar
       "coin"       -> coin, silver coin, coin silver, silver coins,
                       baby silver coin

     Duplication, not navigation. The collections underneath them are what a
     shopper actually wants and never got a look in:

       "coin"        -> Heritage Coins, Devotional Coins, Coins and Bars,
                        Shagun Coins Collection, Gifting Coins & Bars
       "baby"        -> Baby Jewellery, Baby Accessories, Gifts For Baby
       "anniversary" -> Anniversary Gifts, Wedding and Anniversary Gifts
       "corporate"   -> Corporate Gifts

     So the type list drops `query`, leaving that group as collections only,
     which is renamed below to match what it now holds.

     theme.js builds this URL inside the minified bundle, so rather than
     re-implement its fetch-and-render (which would then drift on every theme
     update) this wraps fetch and rewrites the query string. The wrapper is
     deliberately narrow: it touches nothing but /search/suggest.
     ---------------------------------------------------------------- */
  var nativeFetch = window.fetch;
  if (typeof nativeFetch === 'function') {
    window.fetch = function (input, init) {
      try {
        var url = typeof input === 'string' ? input : (input && input.url);
        if (typeof url === 'string' && url.indexOf('/search/suggest') !== -1) {
          var u = new URL(url, window.location.origin);

          u.searchParams.set('resources[limit_scope]', 'each');

          var types = u.searchParams.get('resources[type]');
          if (types) {
            var kept = types.split(',').filter(function (t) {
              return t.trim() !== 'query';
            });
            /* never send an empty type list - fall back to leaving it alone */
            if (kept.length) u.searchParams.set('resources[type]', kept.join(','));
          }

          var patched = u.pathname + u.search;
          if (typeof input === 'string') input = patched;
          else input = new Request(patched, input);
        }
      } catch (e) {
        /* never let this break a fetch - fall through to the original */
      }
      return nativeFetch.apply(this, arguments.length > 1 ? [input, init] : [input]);
    };
  }

  /* Caps. With a per-type limit each group fills its own budget, so hold the
     supporting groups back and let products have the room. Matched on the
     section template's stable ids, not on heading text, which is translated.
     Products are deliberately not capped. */
  var GROUP_CAPS = [
    ['predictiveSearchSuggestions', 4],
    ['predictiveSearchPagesAndBlogPosts', 2]
  ];

  /* That group now holds collections only, so "Suggestions" is the wrong
     word for it - it is a list of categories. Relabelled here rather than in
     locales/en.default.json only because this store is single-locale and the
     rest of the search work already lives in this file; if the store is ever
     translated, move this to the 'general.search.suggestions' key instead. */
  var GROUP_LABEL = 'Collections';

  function capGroups(container) {
    for (var g = 0; g < GROUP_CAPS.length; g++) {
      var group = container.querySelector('[aria-labelledby="' + GROUP_CAPS[g][0] + '"]');
      if (!group) continue;
      var items = group.querySelectorAll('.predictive-search__item');
      for (var i = GROUP_CAPS[g][1]; i < items.length; i++) items[i].remove();
    }

    var heading = container.querySelector('#predictiveSearchSuggestions span');
    if (heading && heading.textContent.trim() !== GROUP_LABEL) heading.textContent = GROUP_LABEL;
  }

  /* ----------------------------------------------------------------
     SEARCH-006 - visible empty state

     sections/api-predictive-search.liquid puts "No results for x" inside
     [data-predictive-search-live-region-count-value], which carries .hidden.
     Screen readers hear it; everyone else gets a blank panel with a
     "View All Results" button leading to an empty page.

     When a render comes back with no result groups, show the message and offer
     the categories as a way out.
     ---------------------------------------------------------------- */
  var EMPTY_CLASS = 'predictive-search__empty';

  function renderEmptyState(container) {
    var wrapper = container.querySelector('[data-search-results-wrapper]');
    if (!wrapper) return;
    if (wrapper.querySelector('.predictive-search__group')) return; /* has results */
    if (wrapper.querySelector('.' + EMPTY_CLASS)) return;           /* already done */

    var popdown = container.closest('search-popdown');
    var input = popdown ? fieldOf(popdown) : null;
    var term = input ? input.value.trim() : '';

    var box = document.createElement('div');
    box.className = EMPTY_CLASS;

    var title = document.createElement('p');
    title.className = 'predictive-search__empty-title';
    title.textContent = term ? 'No matches for “' + term + '”' : 'No matches';

    var hint = document.createElement('p');
    hint.className = 'predictive-search__empty-hint';
    hint.textContent = 'Try a shorter word, or pick a category:';

    box.appendChild(title);
    box.appendChild(hint);

    /* Give people somewhere to go. The popular-searches panel already holds
       the right links, but it is absolutely positioned at top:0 of the same
       box as the results, so un-hiding it would stack the two on top of each
       other. Clone the anchors instead - cloned nodes carry the href but not
       theme.js's click handler, so they navigate natively, which is exactly
       what SEARCH-005 above is forcing anyway. */
    var source = popdown ? popdown.querySelectorAll('[data-popular-searches-link]') : [];
    if (source.length) {
      var list = document.createElement('div');
      list.className = 'predictive-search__empty-links';
      for (var i = 0; i < source.length && i < 6; i++) {
        var a = source[i].cloneNode(true);
        a.removeAttribute('tabindex');
        a.removeAttribute('data-popular-searches-link');
        list.appendChild(a);
      }
      box.appendChild(list);
    }

    wrapper.insertBefore(box, wrapper.firstChild);
  }

  function watchResults(container) {
    if (container.hasAttribute('data-empty-state-watched')) return;
    container.setAttribute('data-empty-state-watched', '');
    new MutationObserver(function () {
      capGroups(container);
      renderEmptyState(container);
    }).observe(container, { childList: true });
  }

  function initEmptyState() {
    var containers = document.querySelectorAll('[data-predictive-search-results]');
    for (var i = 0; i < containers.length; i++) watchResults(containers[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initEmptyState);
  } else {
    initEmptyState();
  }
})();
